import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, MapPin, Tag, Calendar, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { normalizeText, fuzzyMatch, sanitizePostgrestFilter } from "@/lib/search";
import { EVENT_CATEGORIES } from "@/lib/categories";
import { BRAZILIAN_CAPITALS } from "@/lib/cities";
import { useDebounce } from "@/hooks/useDebounce";

interface SearchSuggestion {
  type: "event" | "category" | "city";
  id: string;
  title: string;
  subtitle?: string;
  slug?: string;
}

interface SearchBarProps {
  className?: string;
  variant?: "hero" | "navbar" | "page";
  placeholder?: string;
  autoFocus?: boolean;
}

export function SearchBar({
  className,
  variant = "page",
  placeholder = "Buscar eventos, shows, cidades...",
  autoFocus = false,
}: SearchBarProps) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const debouncedQuery = useDebounce(query, 300);

  // Fetch suggestions
  const fetchSuggestions = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    const results: SearchSuggestion[] = [];

    try {
      // Search categories (local)
      const matchingCategories = EVENT_CATEGORIES.filter((cat) =>
        fuzzyMatch(searchQuery, cat.label, 0.6) ||
        fuzzyMatch(searchQuery, cat.description, 0.6)
      ).slice(0, 2);

      for (const cat of matchingCategories) {
        results.push({
          type: "category",
          id: cat.value,
          title: cat.label,
          subtitle: "Categoria",
          slug: cat.value,
        });
      }

      // Search cities (local)
      const matchingCities = BRAZILIAN_CAPITALS.filter((city) =>
        fuzzyMatch(searchQuery, city.name, 0.6) ||
        fuzzyMatch(searchQuery, city.state, 0.6)
      ).slice(0, 2);

      for (const city of matchingCities) {
        results.push({
          type: "city",
          id: city.uf,
          title: city.name,
          subtitle: city.state,
          slug: city.name,
        });
      }

      // Search events from database
      const safeQuery = sanitizePostgrestFilter(searchQuery);
      const { data: events } = await supabase
        .from("events")
        .select("id, title, slug, venue_city, category, start_date")
        .eq("status", "published")
        .eq("visibility", "public")
        .or(
          `title.ilike.%${safeQuery}%,venue_city.ilike.%${safeQuery}%,category.ilike.%${safeQuery}%`
        )
        .limit(5);

      if (events) {
        for (const event of events) {
          // Additional fuzzy check
          const titleMatch = fuzzyMatch(searchQuery, event.title, 0.5);
          const cityMatch = event.venue_city && fuzzyMatch(searchQuery, event.venue_city, 0.5);
          const categoryMatch = event.category && fuzzyMatch(searchQuery, event.category, 0.5);

          if (titleMatch || cityMatch || categoryMatch) {
            results.push({
              type: "event",
              id: event.id,
              title: event.title,
              subtitle: event.venue_city || "Online",
              slug: event.slug,
            });
          }
        }
      }

      // Remove duplicates and limit
      const uniqueResults = results.filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.type === item.type && t.id === item.id)
      );

      setSuggestions(uniqueResults.slice(0, 8));
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSuggestions(debouncedQuery);
  }, [debouncedQuery, fetchSuggestions]);


  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || suggestions.length === 0) {
      if (e.key === "Enter" && query.trim()) {
        e.preventDefault();
        navigateToSearch();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSuggestionClick(suggestions[selectedIndex]);
        } else {
          navigateToSearch();
        }
        break;
      case "Escape":
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const navigateToSearch = () => {
    if (query.trim()) {
      navigate(`/busca?q=${encodeURIComponent(query.trim())}`);
      setIsOpen(false);
      setQuery("");
    }
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    switch (suggestion.type) {
      case "event": {
        const slug = suggestion.slug || suggestion.id;
        navigate(`/evento/${encodeURIComponent(String(slug))}`);
        break;
      }
      case "category": {
        const cat = suggestion.slug || suggestion.id;
        navigate(`/eventos?categoria=${encodeURIComponent(String(cat))}`);
        break;
      }
      case "city": {
        const city = suggestion.slug || suggestion.id;
        navigate(`/eventos?cidade=${encodeURIComponent(String(city))}`);
        break;
      }
    }
    setIsOpen(false);
    setQuery("");
  };

  const getSuggestionIcon = (type: SearchSuggestion["type"]) => {
    switch (type) {
      case "event":
        return <Calendar className="h-4 w-4 text-gray-500" />;
      case "category":
        return <Tag className="h-4 w-4 text-gray-500" />;
      case "city":
        return <MapPin className="h-4 w-4 text-gray-500" />;
    }
  };

  const variantStyles = {
    hero: "h-14 text-base md:text-lg rounded-2xl pl-6 pr-14 border border-border/50 bg-secondary/40 hover:bg-secondary/60 focus:bg-background/95 backdrop-blur-xl shadow-none",
    navbar: "h-10 text-base md:text-sm rounded-xl pl-4 pr-10 bg-secondary/40 border border-transparent hover:bg-secondary/60 focus:bg-background focus:border-border/60 shadow-none",
    page: "h-12 text-base rounded-2xl pl-5 pr-12 bg-secondary/40 border border-border/50 hover:bg-secondary/60 focus:bg-background focus:border-foreground/20 shadow-none",
  };

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      <div className="relative group">
        <Input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            setSelectedIndex(-1);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className={cn(
            "w-full transition-all duration-300 focus-visible:ring-1 focus-visible:ring-foreground/20 focus-visible:ring-offset-0 shadow-none placeholder:text-muted-foreground/70",
            variantStyles[variant]
          )}
        />
        <div
          className={cn(
            "absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1",
            variant === "hero" && "right-2",
          )}
        >
          {query && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted"
              onClick={() => {
                setQuery("");
                setSuggestions([]);
                inputRef.current?.focus();
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
          <Button
            type="button"
            variant={variant === "hero" ? "default" : "ghost"}
            size="icon"
            className={cn(
              "transition-all duration-200 shadow-none",
              variant === "hero" && "h-10 w-10 md:h-11 md:w-11 rounded-xl",
              variant === "navbar" && "h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground",
              variant === "page" && "h-9 w-9 rounded-xl text-muted-foreground hover:text-foreground"
            )}
            onClick={navigateToSearch}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4 md:h-5 md:w-5" />
            )}
          </Button>
        </div>
      </div>

      {/* Suggestions Dropdown */}
      {isOpen && (suggestions.length > 0 || (query.length >= 2 && !isLoading)) && (
        <div
          style={{ 
            maxHeight: "max(300px, 60vh)"
          }}
          className="absolute left-0 right-0 top-full mt-2 bg-background/95 border border-border/50 rounded-2xl shadow-none z-50 overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200 flex flex-col backdrop-blur-2xl"
        >
          <div className="overflow-y-auto no-scrollbar flex-1">
            {suggestions.length > 0 ? (
              (() => {
                const grouped: Record<string, SearchSuggestion[]> = {
                  category: [],
                  city: [],
                  event: [],
                };

                suggestions.forEach((s) => grouped[s.type].push(s));

                return (
                  <div className="pb-2">
                     {/* Section Category */}
                    {grouped.category.length > 0 && (
                      <div>
                        <div className="px-4 py-2 mt-2 flex items-center gap-2 text-[10px] font-bold text-muted-foreground uppercase tracking-widest bg-muted/30">
                          <Tag className="h-3 w-3" /> Categorias
                        </div>
                        <ul className="py-1">
                          {grouped.category.map((suggestion, i) => (
                            <li key={`category-${suggestion.id}`}>
                              <button
                                type="button"
                                onClick={() => handleSuggestionClick(suggestion)}
                                className={cn(
                                  "w-full px-4 py-3 flex items-center gap-4 text-left hover:bg-muted/50 transition-colors",
                                  selectedIndex === i && "bg-muted"
                                )}
                              >
                                <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                  {getSuggestionIcon(suggestion.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-bold truncate text-sm text-foreground">{suggestion.title}</p>
                                  <p className="text-xs text-muted-foreground truncate">{suggestion.subtitle}</p>
                                </div>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Section City */}
                    {grouped.city.length > 0 && (
                      <div>
                        <div className="px-4 py-2 mt-1 flex items-center gap-2 text-[10px] font-bold text-muted-foreground uppercase tracking-widest bg-muted/30">
                          <MapPin className="h-3 w-3" /> Cidades
                        </div>
                        <ul className="py-1">
                          {grouped.city.map((suggestion, i) => (
                            <li key={`city-${suggestion.id}`}>
                              <button
                                type="button"
                                onClick={() => handleSuggestionClick(suggestion)}
                                className={cn(
                                  "w-full px-4 py-3 flex items-center gap-4 text-left hover:bg-muted/50 transition-colors",
                                  selectedIndex === (grouped.category.length + i) && "bg-muted"
                                )}
                              >
                                <div className="h-9 w-9 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                                  {getSuggestionIcon(suggestion.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-bold truncate text-sm text-foreground">{suggestion.title}</p>
                                  <p className="text-xs text-muted-foreground truncate">{suggestion.subtitle}</p>
                                </div>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Section Event */}
                    {grouped.event.length > 0 && (
                      <div>
                        <div className="px-4 py-2 mt-1 flex items-center gap-2 text-[10px] font-bold text-muted-foreground uppercase tracking-widest bg-muted/30">
                          <Calendar className="h-3 w-3" /> Eventos
                        </div>
                        <ul className="py-1">
                          {grouped.event.map((suggestion, i) => (
                            <li key={`event-${suggestion.id}`}>
                              <button
                                type="button"
                                onClick={() => handleSuggestionClick(suggestion)}
                                className={cn(
                                  "w-full px-4 py-3 flex items-center gap-4 text-left hover:bg-muted/50 transition-colors",
                                  selectedIndex === (grouped.category.length + grouped.city.length + i) && "bg-muted"
                                )}
                              >
                                <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                  {getSuggestionIcon(suggestion.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-bold truncate text-sm text-foreground">{suggestion.title}</p>
                                  <p className="text-xs text-muted-foreground truncate">{suggestion.subtitle}</p>
                                </div>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })()
            ) : (
              <div className="px-6 py-10 text-center">
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                  <Search className="h-6 w-6 text-muted-foreground/40" />
                </div>
                <p className="text-sm font-bold text-foreground">Nenhum resultado para "{query}"</p>
                <p className="text-xs text-muted-foreground mt-1">Verifique a ortografia ou tente outros termos</p>
              </div>
            )}
          </div>

          {query.trim() && (
            <div className="border-t border-border p-3 bg-muted/20">
              <button
                type="button"
                onClick={navigateToSearch}
                className="w-full h-11 px-4 rounded-xl text-sm font-bold bg-primary/5 text-primary hover:bg-primary/10 transition-colors flex items-center justify-center gap-2"
              >
                <Search className="h-4 w-4" />
                Ver todos os resultados para "{query}"
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
